import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

/**
 * BottomSheet Component
 *
 * A mobile-first modal that slides up from the bottom.
 * Supports drag to dismiss, snap points, and backdrop click.
 */

const BottomSheetContext = createContext(null);

/**
 * BottomSheet Provider
 */
export function BottomSheetProvider({ children }) {
  const [sheets, setSheets] = useState([]);

  const openSheet = useCallback((content, options = {}) => {
    const id = Date.now() + Math.random();
    setSheets((prev) => [...prev, { id, content, options }]);
    return id;
  }, []);

  const closeSheet = useCallback((id) => {
    setSheets((prev) => {
      if (id) {
        return prev.filter((s) => s.id !== id);
      }
      // Close the topmost sheet
      return prev.slice(0, -1);
    });
  }, []);

  const closeAll = useCallback(() => {
    setSheets([]);
  }, []);

  return (
    <BottomSheetContext.Provider value={{ openSheet, closeSheet, closeAll }}>
      {children}
      {sheets.map((sheet) => (
        <BottomSheetRenderer
          key={sheet.id}
          id={sheet.id}
          options={sheet.options}
          onClose={() => closeSheet(sheet.id)}
        >
          {sheet.content}
        </BottomSheetRenderer>
      ))}
    </BottomSheetContext.Provider>
  );
}

/**
 * useBottomSheet hook
 */
export function useBottomSheet() {
  const context = useContext(BottomSheetContext);
  if (!context) {
    throw new Error('useBottomSheet must be used within a BottomSheetProvider');
  }
  return context;
}

/**
 * BottomSheet Component (Standalone usage)
 */
export function BottomSheet({
  isOpen,
  onClose,
  children,
  title,
  headerAction,
  snapPoints = ['50%', '90%'],
  initialSnap = 0,
  showHandle = true,
  closeOnBackdrop = true,
  noPadding = false,
  className = '',
}) {
  const [currentSnap, setCurrentSnap] = useState(initialSnap);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  // Get height from snap point
  const getSnapHeight = (snap) => {
    const snapValue = snapPoints[snap] || snapPoints[0];
    if (typeof snapValue === 'string' && snapValue.endsWith('%')) {
      return (parseFloat(snapValue) / 100) * window.innerHeight;
    }
    return parseFloat(snapValue);
  };

  const currentHeight = getSnapHeight(currentSnap);

  // Handle drag start
  const handleDragStart = (e) => {
    setIsDragging(true);
    startY.current = e.touches ? e.touches[0].clientY : e.clientY;
    currentY.current = startY.current;
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!isDragging) return;

    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = clientY - startY.current;
    currentY.current = clientY;

    // Only allow dragging down or up within bounds
    setDragOffset(Math.max(-50, delta)); // Allow slight over-drag up
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);
    const delta = dragOffset;

    // If dragged down significantly, close or snap to smaller size
    if (delta > 100) {
      if (currentSnap === 0) {
        onClose?.();
      } else {
        setCurrentSnap(currentSnap - 1);
      }
    }
    // If dragged up, snap to larger size
    else if (delta < -50 && currentSnap < snapPoints.length - 1) {
      setCurrentSnap(currentSnap + 1);
    }

    setDragOffset(0);
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Reset snap when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentSnap(initialSnap);
      setDragOffset(0);
    }
  }, [isOpen, initialSnap]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="bottom-sheet-backdrop animate-fade-in"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`
          bottom-sheet
          animate-slide-up
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        style={{
          height: currentHeight + (isDragging ? -dragOffset : 0),
          transition: isDragging ? 'none' : 'height 0.3s ease-out',
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* Handle */}
        {showHandle && (
          <div
            className="cursor-grab active:cursor-grabbing touch-none select-none py-2"
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="bottom-sheet-handle" />
          </div>
        )}

        {/* Title */}
        {title && (
          <div className="px-5 pb-3 border-b border-warm-100 flex items-center justify-between">
            <h2 className="font-display text-xl text-warm-900">{title}</h2>
            {headerAction && <div>{headerAction}</div>}
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 ${noPadding ? 'flex flex-col overflow-hidden' : 'overflow-y-auto overscroll-contain px-5 py-4'}`}>
          {children}
        </div>
      </div>
    </>
  );
}

/**
 * Internal renderer for context-based sheets
 */
function BottomSheetRenderer({ id, options, onClose, children }) {
  return (
    <BottomSheet
      isOpen={true}
      onClose={onClose}
      title={options.title}
      snapPoints={options.snapPoints}
      initialSnap={options.initialSnap}
      showHandle={options.showHandle}
      closeOnBackdrop={options.closeOnBackdrop}
      className={options.className}
    >
      {typeof children === 'function' ? children({ close: onClose }) : children}
    </BottomSheet>
  );
}

export default BottomSheet;
